/**
 * @file schemas.ts
 * @brief Zod schemas for the Super Admin → Invoices feature (platform-wide).
 *
 * @details
 * Mirrors the company-scoped invoice schemas but adds a `company_id` tenant
 * filter, since the super-admin feed spans every company. Both surfaces read
 * the same `GET /api/invoices` endpoint — the backend returns a platform-wide
 * feed when the caller is a superadmin (else it scopes to their own company).
 *
 * @dependencies
 * - zod: Runtime response validation
 *
 * @references
 * - BACKEND/src/features/invoices/routes.js (superadmin branch of GET /)
 * - WEBPANEL/src/features/company-invoices/schemas.ts (company-scoped sibling)
 *
 * @author DES
 * @date 08-07-2026
 */

import { z } from "zod";

const numericString = z.union([
  z.number(),
  z
    .string()
    .transform((v) => (v === "" ? 0 : Number(v)))
    .pipe(z.number()),
]);

// See company-invoices/schemas.ts — one shared copy of both. (24-08-2026)
export {
  invoicePeriodRange,
  resolveInvoiceTotals,
  isMixedCurrency,
} from "@/shared/utils/invoiceTotals";
export type { InvoicePeriod } from "@/shared/utils/invoiceTotals";
import type { InvoicePeriod } from "@/shared/utils/invoiceTotals";

export const AdminInvoiceRowSchema = z
  .object({
    id: z.string(),
    product: z.string().optional(),
    amount: numericString,
    status: z.string(),
    currency: z.string().nullable().optional(),
    createdAt: z.string().optional(),
    created_at: z.string().nullable().optional(),
    ownerEmail: z.string().nullable().optional(),
    ownerName: z.string().nullable().optional(),
    ownerAvatarUrl: z.string().nullable().optional(),
    billedToCompany: z.boolean().optional(),
    companyId: z.string().nullable().optional(),
    companyName: z.string().nullable().optional(),
    isMine: z.boolean().optional(),
  })
  .passthrough();

export type AdminInvoiceRow = z.infer<typeof AdminInvoiceRowSchema>;

import { InvoiceCurrencyTotalSchema } from "@/features/company-invoices/schemas";

export const AdminInvoicesResponseSchema = z.object({
  totalAmountCents: numericString.optional(),
  // (24-08-2026) The platform feed spans tenants, so it spans CURRENCIES —
  // `totalsByCurrency` is the only correct source for a headline amount here.
  usageTotal: numericString.optional(),
  subscriptionAmountCents: numericString.optional(),
  openAmountCents: numericString.optional(),
  totalsByCurrency: z.array(InvoiceCurrencyTotalSchema).optional(),
  total: numericString.optional(),
  page: z.number().optional(),
  pageSize: z.number().optional(),
  items: z.array(AdminInvoiceRowSchema).default([]),
});

export type AdminInvoicesResponse = z.infer<typeof AdminInvoicesResponseSchema>;

export type AdminInvoicesListParams = {
  page?: number;
  pageSize?: number;
  q?: string;
  from?: string;
  to?: string;
  company_id?: string;
  period?: InvoicePeriod;
};

export function invoiceCreatedAt(row: AdminInvoiceRow): string | null {
  return row.createdAt ?? row.created_at ?? null;
}

export function invoiceOwnerLabel(row: AdminInvoiceRow): string {
  if (row.ownerName?.trim()) return row.ownerName.trim();
  if (row.ownerEmail) return row.ownerEmail;
  return "—";
}

export function invoiceOwnerKey(row: AdminInvoiceRow): string {
  if (row.ownerEmail?.trim()) return row.ownerEmail.trim().toLowerCase();
  return invoiceOwnerLabel(row).toLowerCase();
}

export function adminInvoicesPagination(
  data: AdminInvoicesResponse | undefined,
  params: AdminInvoicesListParams,
) {
  const pageSize = params.pageSize ?? 25;
  const page = params.page ?? 1;
  const total = Number(data?.total ?? 0);
  return {
    page,
    limit: pageSize,
    total,
    totalPages: Math.max(1, Math.ceil(total / pageSize)),
  };
}
